import { z } from 'zod';

import { aggregateType, dataSourceTypeSchema, joinType } from '../schema';

export const datasourceRelationshipType = z.enum([
  'parent',
  'child',
  'sibling',
]);

export const datasourceRequestSchema = z.object({
  type: dataSourceTypeSchema,
  joinType: joinType.nullish(),
  /**
   * Index of "left" datasource to join to
   * In the future, we could ideally do with renaming this to leftDatasourceIndex or something
   * unrelated to child/parent which is defined with relationshipType
   */
  parentIndex: z.number().int().nullish(),

  /**
   * How this datasource related to its parentIndex datasource
   * e.g. a risk can be joined to another risk as a parent or a child
   * By default, this will be "child" i.e. this datasource is a child of parentIndex
   */
  relationshipToParentIndex: datasourceRelationshipType.nullish(),

  /**
   * Only return the latest record for each parent
   */
  latest: z.boolean().nullish(),
});

// Add sibling in the future!

export type DatasourceRelationshipType = z.infer<
  typeof datasourceRelationshipType
>;

export const datasourceFieldSchema = z.object({
  fieldId: z.string(),
  dataSourceIndex: z.number().int(),
});

export const operatorSchema = z.enum([
  '=',
  '!=',
  '<',
  '>',
  '<=',
  '>=',
  'contains', // for backwards compatability
  ':',
  '!:',
]);

export type Operator = z.infer<typeof operatorSchema>;

export const groupByDatePrecision = z.enum(['day', 'month', 'year']);

export type GroupByDatePrecision = z.infer<typeof groupByDatePrecision>;

/**
 * Abuse ceilings for the reporting query input (RSP-6504).
 *
 * Reporting is deliberately a powerful, flexible query tool, and these are NOT
 * product limits — every one is set far above anything the UI can express, so
 * a legitimate report never meets them. They exist because the procedures are
 * `authedProcedure` (no object-permission gate), which makes an unbounded
 * query shape reachable by any authenticated user of an org with the module:
 *
 *  - each `dataSources` entry emits one or two INNER JOINs, and repeated
 *    identical entries are legal (each gets its own alias), so an unbounded
 *    array is an attacker-chosen join explosion — 300 entries compiled to 600
 *    joins in under 1KB of request body;
 *  - `limit`/`offset` were plain `z.number()`, so a fractional, negative or
 *    billion-row value reached Postgres verbatim;
 *  - the recursive filter AST had no depth bound, so ~700 levels of nesting
 *    (a ~22KB body) overflowed the stack inside zod's own parse.
 */
const MAX_DATA_SOURCES = 25;
const MAX_FIELDS = 500;
const MAX_GROUP_BY = 50;
/** The CSV export pages in 5,000-row chunks up to a 100,000-row cap. */
const MAX_LIMIT = 100_000;
const MAX_OFFSET = 10_000_000;
const MAX_FILTER_VALUE_LENGTH = 4_096;
/**
 * Nesting depth of the filter AST. The UI builds two levels (a group of
 * groups); anything beyond a handful is machine-generated.
 */
const MAX_FILTER_DEPTH = 20;

export const filterSchema = z.object({
  field: datasourceFieldSchema,
  value: z.string().max(MAX_FILTER_VALUE_LENGTH).or(z.number()).or(z.null()),
  operator: operatorSchema,
});

const filterBaseSchema = z.object({
  operation: z.enum(['and', 'or']),
});

export type FilterGroup = z.infer<typeof filterBaseSchema> & {
  filters: (Filter | FilterGroup)[];
};

/**
 * Depth-bounded recursive filter group.
 *
 * The bound is enforced by building a finite chain of schemas rather than with
 * a `superRefine` that walks the tree: zod recurses into `z.lazy` as it parses,
 * so an unbounded schema blows the JS stack *during validation* — before any
 * refinement could run — surfacing as a `RangeError` rather than a 400. A
 * group at the innermost level accepts only leaf filters, so over-deep input
 * fails as an ordinary validation error.
 */
const buildFilterGroupSchema = (depth: number): z.ZodType<FilterGroup> =>
  filterBaseSchema.extend({
    filters:
      depth <= 1
        ? z.array(filterSchema)
        : z.lazy(() =>
            z.array(buildFilterGroupSchema(depth - 1).or(filterSchema))
          ),
  });

export const filterGroupSchema: z.ZodType<FilterGroup> =
  buildFilterGroupSchema(MAX_FILTER_DEPTH);

export type Filter = z.infer<typeof filterSchema>;

export type DataSourceField = z.infer<typeof datasourceFieldSchema>;

export type DataSourceRequest = z.infer<typeof datasourceRequestSchema>;

const groupBySchema = z.object({
  field: datasourceFieldSchema,
  datePrecision: groupByDatePrecision.nullish(),
});

export type GroupBy = z.infer<typeof groupBySchema>;

export const reportingDataInputSchema = z
  .object({
    dataSources: z.array(datasourceRequestSchema).max(MAX_DATA_SOURCES),
    fields: z.array(datasourceFieldSchema).max(MAX_FIELDS),
    filters: filterGroupSchema,
    groupBy: groupBySchema.array().max(MAX_GROUP_BY).nullish(),
    aggregateType: aggregateType.nullish(),
    aggregateField: datasourceFieldSchema.nullish(),
    limit: z.number().int().nonnegative().max(MAX_LIMIT),
    offset: z.number().int().nonnegative().max(MAX_OFFSET),
  })
  .superRefine((values, ctx) => {
    if (!values.aggregateType && values.fields.length < 1) {
      ctx.addIssue({
        path: ['fields'],
        code: 'too_small',
        message: 'Required',
        minimum: 1,
        type: 'array',
        inclusive: true,
      });
    }
  });

export type ReportingDataInput = z.infer<typeof reportingDataInputSchema>;

export const PostSchema = z.object({
  Input: reportingDataInputSchema,
});

/**
 * Input contract for `reportingFilterOptions` — filter-option suggestions for a
 * single field of a datasource. Shared verbatim by the tRPC router and the
 * data-layer internal processor (the tRPC migration of the legacy
 * `getReportingFilterOptions` Hasura backend action, RSP-6504).
 */
export const reportingFilterOptionsInputSchema = z.object({
  dataSourceType: dataSourceTypeSchema,
  fieldId: z.string(),
  limit: z.number().int().nonnegative().max(MAX_LIMIT),
  offset: z.number().int().nonnegative().max(MAX_OFFSET),
  filteringText: z.string().max(MAX_FILTER_VALUE_LENGTH),
});

export type ReportingFilterOptionsInput = z.infer<
  typeof reportingFilterOptionsInputSchema
>;
